import { useState, useEffect } from "react";

interface PoolPrice {
  dex: string;
  pair: string;
  price: number;
  liquidity: number;
  fee: number;
}

interface ArbitrageOpportunity {
  buyDex: string;
  sellDex: string;
  pair: string;
  buyPrice: number;
  sellPrice: number;
  spread: number;
  spreadPercent: number;
  netProfit: number;
  viable: boolean;
}

// Cost constants
const COSTS = {
  raydiumFee: 0.0025,
  orcaFee: 0.0005,
  jupiterFee: 0.001,
  priorityFee: 0.001,
  jitoTip: 0.001,
  expectedSlippage: 0.002,
  solPrice: 150,
};

// Fetch SOL price from Binance
async function getSolPrice(): Promise<number> {
  try {
    const res = await fetch("https://api.binance.com/api/v3/ticker/price?symbol=SOLUSDT");
    const json = await res.json() as { price: string };
    return parseFloat(json.price);
  } catch {
    return COSTS.solPrice;
  }
}

// Get prices from different DEXes
async function getDexPrices(pair: string = "SOL/USDC"): Promise<PoolPrice[]> {
  const solPrice = await getSolPrice();
  const prices: PoolPrice[] = [];

  // Simulate small price differences (in production, fetch from actual DEX APIs)
  prices.push({
    dex: "raydium",
    pair,
    price: solPrice * (1 - Math.random() * 0.005),
    liquidity: 50000000,
    fee: COSTS.raydiumFee,
  });

  prices.push({
    dex: "orca",
    pair,
    price: solPrice * (1 + Math.random() * 0.005),
    liquidity: 30000000,
    fee: COSTS.orcaFee,
  });

  prices.push({
    dex: "jupiter",
    pair,
    price: solPrice,
    liquidity: 100000000,
    fee: COSTS.jupiterFee,
  });

  return prices;
}

// Calculate if arbitrage is profitable
function calculateOpportunity(
  buyPool: PoolPrice,
  sellPool: PoolPrice,
  tradeSize: number = 1000
): ArbitrageOpportunity {
  const spread = sellPool.price - buyPool.price;
  const spreadPercent = (spread / buyPool.price) * 100;

  const buyFee = tradeSize * buyPool.fee;
  const sellFee = tradeSize * sellPool.fee;
  const slippageCost = tradeSize * COSTS.expectedSlippage;
  const priorityFeeSol = COSTS.priorityFee * COSTS.solPrice;
  const jitoTip = COSTS.jitoTip * COSTS.solPrice;

  const totalCosts = buyFee + sellFee + slippageCost + priorityFeeSol + jitoTip;
  const grossProfit = (tradeSize * spreadPercent) / 100;
  const netProfit = grossProfit - totalCosts;

  const viable =
    netProfit > 0.5 &&
    spreadPercent > (buyPool.fee + sellPool.fee + COSTS.expectedSlippage) * 100;

  return {
    buyDex: buyPool.dex,
    sellDex: sellPool.dex,
    pair: buyPool.pair,
    buyPrice: buyPool.price,
    sellPrice: sellPool.price,
    spread,
    spreadPercent,
    netProfit,
    viable,
  };
}

// Scan for opportunities
async function scanArbitrageOpportunities(): Promise<ArbitrageOpportunity[]> {
  const prices = await getDexPrices("SOL/USDC");
  const opportunities: ArbitrageOpportunity[] = [];

  for (let i = 0; i < prices.length; i++) {
    for (let j = 0; j < prices.length; j++) {
      if (i !== j && prices[j].price > prices[i].price) {
        const opp = calculateOpportunity(prices[i], prices[j]);
        opportunities.push(opp);
      }
    }
  }

  opportunities.sort((a, b) => b.netProfit - a.netProfit);
  return opportunities;
}

export default function ArbitrageMonitor() {
  const [opportunities, setOpportunities] = useState<ArbitrageOpportunity[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const opps = await scanArbitrageOpportunities();
      setOpportunities(opps);
      setLastUpdate(new Date());
      setLoading(false);
    };

    fetchData();
    const interval = setInterval(fetchData, 10000); // Refresh every 10s

    return () => clearInterval(interval);
  }, []);

  const viable = opportunities.filter((o) => o.viable);

  return (
    <div className="bg-gray-800 rounded-xl border border-gray-700 p-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-bold flex items-center gap-2">
            🔄 Solana Arbitrage Scanner
          </h2>
          <p className="text-gray-400 text-sm">
            Cross-DEX price monitoring (SOL/USDC)
          </p>
        </div>
        <div className="text-right">
          {lastUpdate && (
            <p className="text-xs text-gray-500">
              Last update: {lastUpdate.toLocaleTimeString()}
            </p>
          )}
          {loading && <p className="text-xs text-blue-400">Scanning...</p>}
        </div>
      </div>

      {viable.length > 0 ? (
        <div className="space-y-3">
          {viable.map((opp, i) => (
            <div
              key={i}
              className="bg-green-900/20 border border-green-700 rounded-lg p-3"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="font-bold text-green-400">
                  ✅ Viable Opportunity #{i + 1}
                </span>
                <span className="text-green-400 font-bold">
                  +${opp.netProfit.toFixed(2)} est. profit
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="bg-gray-900/50 p-2 rounded">
                  <span className="text-gray-400">Buy</span>
                  <p className="font-medium uppercase">{opp.buyDex}</p>
                  <p className="text-gray-300">${opp.buyPrice.toFixed(2)}</p>
                </div>
                <div className="bg-gray-900/50 p-2 rounded">
                  <span className="text-gray-400">Sell</span>
                  <p className="font-medium uppercase">{opp.sellDex}</p>
                  <p className="text-gray-300">${opp.sellPrice.toFixed(2)}</p>
                </div>
              </div>
              <div className="mt-2 flex gap-4 text-xs text-gray-400">
                <span>Spread: {opp.spreadPercent.toFixed(3)}%</span>
                <span>Δ: ${opp.spread.toFixed(2)}</span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-6">
          <p className="text-gray-400 mb-2">❌ No profitable opportunities</p>
          <p className="text-xs text-gray-500">
            Spread too small to cover fees + slippage
          </p>
        </div>
      )}

      {/* Show top opportunities even if not viable */}
      {opportunities.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-700">
          <h3 className="text-sm font-semibold text-gray-400 mb-2">
            📊 All Opportunities
          </h3>
          <div className="space-y-1">
            {opportunities.slice(0, 5).map((opp, i) => (
              <div
                key={i}
                className={`flex justify-between text-sm ${
                  opp.viable ? "text-green-400" : "text-gray-500"
                }`}
              >
                <span>
                  {opp.buyDex.toUpperCase()} → {opp.sellDex.toUpperCase()}
                </span>
                <span>
                  {opp.spreadPercent.toFixed(3)}% (${opp.netProfit.toFixed(2)})
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Info box */}
      <div className="mt-4 p-3 bg-blue-900/20 border border-blue-700 rounded-lg text-xs text-gray-300">
        <p className="font-semibold text-blue-400 mb-1">💡 How it works</p>
        <p>
          Monitors prices across Raydium, Orca, and Jupiter. Shows opportunities
          where spread covers DEX fees + ~0.2% slippage + priority fees.
        </p>
        <p className="mt-1 text-gray-400">
          Note: Simulated prices for demo. Production needs real DEX API
          connections.
        </p>
      </div>
    </div>
  );
}